import React from 'react';
import { resume } from '../data/resume';
import { RESUME_DOWNLOAD_NAME, RESUME_PATH } from '../data/site';
import usePageMeta from '../hooks/usePageMeta';
import '../styles/Resume.css';

const Band = ({ id, title, children }) => (
  <section className="resume-band" aria-labelledby={id}>
    <h2 id={id} className="label label-accent">
      {title}
    </h2>
    <div className="resume-band-body">{children}</div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="resume-bullets">
    {items.map((item) => (
      <li key={item}>{item}</li>
    ))}
  </ul>
);

const Resume = () => {
  usePageMeta('Resume', resume.summary[0]);
  const { contact } = resume;

  return (
    <div className="resume">
      <header className="page-header resume-header container">
        <div className="resume-header-main">
          <span className="label">Resume</span>
          <h1>{resume.name}</h1>
          <ul className="resume-roles" aria-label="Roles">
            {resume.roles.map((role) => (
              <li key={role}>{role}</li>
            ))}
          </ul>
          <ul className="resume-contact">
            <li>{contact.location}</li>
            <li>
              <a href={`mailto:${contact.email}`}>{contact.email}</a>
            </li>
            <li>
              <a href={`tel:${contact.phone.replace(/-/g, '')}`}>{contact.phone}</a>
            </li>
            <li>
              <a href={contact.github.href} target="_blank" rel="noopener noreferrer">
                {contact.github.label}
              </a>
            </li>
          </ul>
        </div>
        <div className="resume-actions">
          <a href={RESUME_PATH} download={RESUME_DOWNLOAD_NAME} className="primary-button">
            Download PDF
          </a>
        </div>
      </header>

      <div className="container resume-body">
        <Band id="resume-summary" title="Summary">
          {resume.summary.map((para) => (
            <p key={para} className="resume-summary">
              {para}
            </p>
          ))}
        </Band>

        <Band id="resume-builds" title="Selected Builds">
          {resume.builds.map((build) => (
            <article key={build.title} className="resume-entry">
              <h3>
                {build.title} <span className="resume-meta">— {build.status}</span>
              </h3>
              <Bullets items={[...build.bullets, `Stack: ${build.stack}`]} />
            </article>
          ))}
        </Band>

        <Band id="resume-experience" title="Professional Experience">
          {resume.experience.map((job) => (
            <article key={job.company} className="resume-entry">
              <h3>
                {job.role} — {job.company} — {job.location}{' '}
                <span className="resume-meta">· {job.dates}</span>
              </h3>
              {job.intro && <p className="resume-intro">{job.intro}</p>}
              <Bullets items={job.bullets} />
            </article>
          ))}
        </Band>

        <Band id="resume-skills" title="Technical Skills">
          <dl className="resume-skills">
            {resume.skills.map((skill) => (
              <div key={skill.label}>
                <dt>{skill.label}:</dt> <dd>{skill.items}</dd>
              </div>
            ))}
          </dl>
        </Band>

        <Band id="resume-education" title="Education & Certifications">
          <ul className="resume-bullets">
            {resume.education.map((item) => (
              <li key={item.title}>
                <strong>{item.title}</strong> {item.detail.startsWith('(') ? item.detail : `— ${item.detail}`}
              </li>
            ))}
          </ul>
        </Band>

        <Band id="resume-more" title="More Projects">
          <ul className="resume-bullets">
            {resume.moreProjects.map((project) => (
              <li key={project.title}>
                <strong>
                  {project.href ? (
                    <a href={project.href} target="_blank" rel="noopener noreferrer">
                      {project.title}
                    </a>
                  ) : (
                    project.title
                  )}
                  :
                </strong>{' '}
                {project.text}
              </li>
            ))}
          </ul>
        </Band>
      </div>
    </div>
  );
};

export default Resume;
